import { api } from '../../lib/api'
import {
  PENDING_STOP_KEY, VIEW_AS_HEADER, clearViewAsGrant, viewAsGrant,
} from '../../lib/viewAs'

export { PENDING_STOP_KEY }

/** End THIS TAB's "View as". A full reload rather than a state update:
 *  every page was rendered as the target, and there is no way to know which
 *  of them cached what. Reloading is the only honest way back.
 *
 *  The tab drops its grant first, so the way back never depends on the
 *  server (a stop that did once left signing out as the only exit — prod,
 *  2026-10-16). But the server's stop is what REVOKES the grant (copies in
 *  history or another window die with it) and writes the audit row, so a
 *  stop that fails is not forgotten: the grant is queued and sent again on
 *  this tab's next page load (retryPendingStop) until the server takes it.
 *  Other tabs keep theirs. */
export async function stopImpersonating(): Promise<void> {
  const grant = viewAsGrant()
  clearViewAsGrant()
  if (!(await sendStop(grant)) && grant) {
    window.sessionStorage.setItem(PENDING_STOP_KEY, grant)
  }
  window.location.reload()
}

/** Send a stop queued by a failed stopImpersonating — at boot, before the
 *  page's own requests. Cleared once the server records it; kept (and said
 *  again) while it cannot. */
export async function retryPendingStop(): Promise<void> {
  const grant = window.sessionStorage.getItem(PENDING_STOP_KEY)
  if (!grant) return
  if (await sendStop(grant)) window.sessionStorage.removeItem(PENDING_STOP_KEY)
}

async function sendStop(grant: string | null): Promise<boolean> {
  try {
    // explicitly this grant's stop: by now the tab itself holds none
    const r = await api<{ stopped?: boolean }>('/api/v1/admin/impersonate', grant
      ? { method: 'DELETE', headers: { [VIEW_AS_HEADER]: grant } }
      : { method: 'DELETE' })
    if (grant && r?.stopped === false) {
      // the server had nothing live to stop: the grant was already dead
      // (stopped, signed out, expired, or another person's) — done, but
      // not a recorded stop, and said as such
      console.info('View as: that grant had already ended; no stop to record.')
    }
    return true
  } catch (e) {
    console.warn('View as ended in this tab, but the server has not yet '
                 + 'recorded the stop; it will be sent again on the next page '
                 + 'load (until then the grant is not revoked):', e)
    return false
  }
}
